/**
 * Live Trading V3 — Spacebar Pull All, scoped to the FOCUSED GAME.
 *
 * One keyboard action that removes the trader's resting Kalshi exposure on
 * the game they are aimed at — and nothing else. The dangerous half of that
 * sentence is "and nothing else", so this module deliberately writes no new
 * cancellation logic at all. It is a keyboard entry point into the exact
 * machinery every mouse sweep already uses:
 *
 *   selectExactGameOrders  — the #272 exact-game selection: an order is a
 *     target only when its market resolves through marketStore's fail-closed
 *     canonical-key resolver to the SAME five-part game key. Reads the
 *     authoritative open-order state, never DOM rows, so off-screen and
 *     collapsed orders are included by construction. Anything that ties to
 *     the matchup but cannot be assigned to the exact game BLOCKS the whole
 *     action — cancelling "the easy subset" silently leaves exposure the
 *     trader believes is gone.
 *   freezeCancelSnapshot   — the immutable set the DELETEs are sent from.
 *   executeBulkOrderCancel — the ONE mixed-venue executor, with the #241
 *     cancel fence, per-order results, and retry-failed-only semantics.
 *
 * Boundaries in this release:
 *   • KALSHI ONLY — the hotkey lane is Kalshi-only everywhere else, and the
 *     pull is no wider than the keys that build the exposure. Polymarket
 *     resting orders on the game are NOT touched, and the summary does not
 *     pretend otherwise (the mouse "Cancel this game" sweep remains the
 *     mixed-venue tool).
 *   • Resting orders only. Positions are never flattened.
 *   • PINNED orders are skipped (#943): the trader's pinned Kalshi orders are
 *     subtracted at freeze time and the summary names how many were left.
 *   • One pull at a time: a second Spacebar while one is pending refuses
 *     rather than duplicating cancellation traffic.
 *   • Every pull is AUTHORIZED server-side before any cancel is sent: the
 *     live_pull_all attempt record (game scope, exact frozen targets) is
 *     written first, and the relay refuses it when the LT3_PULL_ALL kill
 *     switch is off or the trader's LT2 grant is revoked. A relay REFUSAL
 *     (any 4xx: 403 kill switch / grant, 400, 401) refuses the WHOLE pull
 *     before a cancel is sent — the packet's separate kill boundary for the
 *     one-key action. B5 (approved Oct 7): when only the audit WRITE fails
 *     (5xx, a timeout, a network error) the pull still cancels, like the
 *     mouse sweeps, and the summary says "audit degraded". A trader pressing
 *     the kill key during a relay hiccup gets their cancels.
 *   • D2: the summary counts cancels SENT, never "pulled" (a DELETE the
 *     venue accepted is not proof the order is gone), names the orders NOT
 *     cancelled, and says when Polymarket orders on the same game are still
 *     resting. The caller turns any of those into an amber partial result.
 */
import { useAccountStore } from '@/stores/accountStore';
import { useAuthStore } from '@/stores/authStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { pinnedKalshiOrderIds, useTrackerPins } from '@/stores/trackerPins';
import {
  selectExactGameOrders,
  freezeCancelSnapshot,
} from '@/lib/trading/gameOrderSelection';
import { executeBulkOrderCancel } from '@/lib/trading/bulkOrderCancel';
import {
  buildPullAllAttemptBody,
  CANCEL_ALL_AUDIT_TIMEOUT_MS,
} from '@/lib/trading/bulkCancelAttemptAudit';
import { apiFetch } from '@/stores/authStore';
import { usePolyRelayAccountStore } from '@/stores/polyRelayAccountStore';
import type { FrozenCancelSnapshot } from '@/lib/trading/gameOrderSelection';
import type { GameRow } from '@/views/ValueDashboard/types';
import type { KalshiOrder } from '@/types/kalshi';

export interface PullAllOutcome {
  status: 'refused' | 'empty' | 'done';
  /** One ack-line sentence — the strip is the trader's only receipt. For a
   *  'done' pull it is the result without the game, which `gameLabel`
   *  carries: "2/2 cancels sent", or "1 NOT cancelled, 1 cancel sent —
   *  check open orders". */
  message: string;
  /** The game the pull was scoped to, e.g. "DAL @ GSV" (empty when the pull
   *  was refused before a game was resolved). */
  gameLabel: string;
  requested: number;
  /** DELETEs the venue accepted. Not proof the orders are gone. */
  submitted: number;
  /** Cancels that failed: these orders are still resting. */
  failed: number;
  /** Orders already being cancelled by an earlier request: neutral. */
  alreadyPending: number;
  /** What went wrong around the cancels, each named for the summary:
   *  'refresh degraded' (open orders were not re-read after), 'audit
   *  degraded' (the attempt record was not written, B5). */
  degraded: string[];
  /** Polymarket orders on the same game that Pull All left resting (it is
   *  Kalshi-only). 0 when none, or when Polymarket orders were never read. */
  polyResting: number;
  /** The relay's code on a refused authorization (e.g. PULL_ALL_DISABLED,
   *  LT2_NOT_ENABLED), for the caller to act on. */
  code?: string;
}

function outcome(
  status: PullAllOutcome['status'],
  message: string,
  fields: Partial<PullAllOutcome> = {},
): PullAllOutcome {
  return {
    status,
    message,
    gameLabel: '',
    requested: 0,
    submitted: 0,
    failed: 0,
    alreadyPending: 0,
    degraded: [],
    polyResting: 0,
    ...fields,
  };
}

type Authorization =
  | { kind: 'ok' }
  | { kind: 'refused'; message: string; code?: string }
  | { kind: 'degraded' };

/**
 * The pull's server authorization (the live_pull_all attempt record). The
 * relay ANSWERING with a refusal (4xx) refuses the pull. A write that fails
 * (5xx), a timeout or a lost connection is an audit failure, not a refusal:
 * the pull proceeds and reports it (B5).
 */
async function authorizePull(snapshot: FrozenCancelSnapshot & { scope: 'game' }): Promise<Authorization> {
  const controller = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<'timeout'>((resolve) => {
    timeout = setTimeout(() => {
      controller.abort();
      resolve('timeout');
    }, CANCEL_ALL_AUDIT_TIMEOUT_MS);
  });
  try {
    const res = await Promise.race([
      apiFetch('/api/kalshi/orders/cancel-all-attempt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPullAllAttemptBody(snapshot)),
        signal: controller.signal,
      }),
      timedOut,
    ]);
    if (res === 'timeout') return { kind: 'degraded' };
    if (res.ok) return { kind: 'ok' };
    if (res.status >= 500) return { kind: 'degraded' };
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const error = typeof body.error === 'string' && body.error !== '' ? body.error : null;
    const code = typeof body.code === 'string' && body.code !== '' ? body.code : undefined;
    return {
      kind: 'refused',
      message: error ?? `relay did not authorize the pull (${res.status})`,
      ...(code != null ? { code } : {}),
    };
  } catch {
    return { kind: 'degraded' };
  } finally {
    if (timeout !== undefined) clearTimeout(timeout);
  }
}

/** D2 — Polymarket orders on `game` that a Kalshi-only pull leaves resting,
 *  from the relay-held list every authenticated route keeps current. Never
 *  read means not counted: the summary claims nothing either way. */
function polyRestingOn(game: GameRow, tickerFallback: boolean): number {
  const relay = usePolyRelayAccountStore.getState();
  if (!relay.ordersKnown || relay.orders.length === 0) return 0;
  const poly = selectExactGameOrders({
    game,
    kalshiOrders: [],
    polyOrders: relay.orders,
    tickerFallback,
  });
  return (
    poly.targets.filter((t) => t.venue === 'polymarket').length +
    poly.blockers.filter((b) => b.venue === 'polymarket').length
  );
}

let _pullInFlight = false;

export function isPullAllPending(): boolean {
  return _pullInFlight;
}

export function _resetPullAllForTests(): void {
  _pullInFlight = false;
}

/**
 * `onlyOrders`: sweep exactly these orders instead of the account's open
 * orders, still only where each provably belongs to `game` (the same exact
 * selection by ticker, the same server authorization, the same cancel path).
 * The hotkey listener's second pass uses it for the orders a press reported
 * as accepted after the first pass. They are named by the press itself,
 * because an order the venue just accepted is not in open orders yet (the V2
 * create response carries no status, so it appears only on the next refresh);
 * and an order the trader placed AFTER pressing Pull All is never swept.
 */
export async function executeFocusedGamePull(
  game: GameRow,
  opts: { onlyOrders?: ReadonlyArray<{ orderId: string; ticker: string }> } = {},
): Promise<PullAllOutcome> {
  if (_pullInFlight) {
    return outcome('refused', 'Pull All already pending — wait for the result');
  }

  const account = useAccountStore.getState();
  const tickerFallback = useDisplaySettings.getState().gameCancelTickerFallback;
  const selection = selectExactGameOrders({
    game,
    kalshiOrders:
      opts.onlyOrders != null
        ? opts.onlyOrders.map((o) => ({ orderId: o.orderId, ticker: o.ticker }) as KalshiOrder)
        : account.openOrders ?? [],
    // Kalshi-only in this release — see the module contract above.
    polyOrders: [],
    tickerFallback,
  });
  const gameLabel = selection.gameLabel;

  if (selection.blockers.length > 0) {
    // Fail closed on the WHOLE action: a partially attributable game must
    // not be swept partially. The blocker messages name the orders.
    return outcome(
      'refused',
      `Pull refused — ${selection.blockers.length} order(s) could not be safely assigned to ${gameLabel}`,
      { gameLabel },
    );
  }

  const snapshot = freezeCancelSnapshot({
    scope: 'game',
    label: selection.gameLabel,
    targets: selection.targets,
    pinnedKalshiOrderIds: pinnedKalshiOrderIds(
      useTrackerPins.getState(),
      useAuthStore.getState().user?.id ?? null,
    ),
  });
  const skipped = snapshot.skippedPinned.length;

  // Only the account-wide pass counts other venues; the second pass names
  // exactly one press's orders.
  const polyResting = opts.onlyOrders == null ? polyRestingOn(game, tickerFallback) : 0;
  const polyHint =
    polyResting > 0 ? ` · ${polyResting} Polymarket still resting — use Cancel this game` : '';

  if (snapshot.targets.length === 0) {
    return outcome(
      'empty',
      (skipped > 0
        ? `No unpinned resting Kalshi orders on ${gameLabel} (skipped ${skipped} pinned)`
        : `No resting Kalshi orders on ${gameLabel}`) + polyHint,
      { gameLabel, polyResting },
    );
  }

  _pullInFlight = true;
  try {
    // SERVER AUTHORIZATION first — the kill boundary. A relay refusal (403
    // from the kill switch or a revoked grant, any other 4xx) refuses the
    // whole pull before a single cancel is sent. A failed audit write is
    // reported, not obeyed (B5).
    const authorization = await authorizePull(snapshot);
    if (authorization.kind === 'refused') {
      return outcome('refused', `Pull refused — ${authorization.message}`, {
        gameLabel,
        ...(authorization.code != null ? { code: authorization.code } : {}),
      });
    }
    const report = await executeBulkOrderCancel(snapshot, {
      cancelKalshiOrders: (ids) => account.cancelOrders([...ids], { refreshAfter: false }),
      // No Polymarket targets can exist in this snapshot (see above); the
      // executor still requires the dep, and reaching it would be a bug.
      requestPolymarketCancel: () => {
        throw new Error('Pull All is Kalshi-only');
      },
      refreshKalshi: () => account.refreshOrders(),
      refreshPolymarket: async () => {},
      // Authorization above already recorded the attempt.
    });

    const submitted = report.submitted.length;
    const failed = report.failed.length;
    const alreadyPending = report.alreadyPending.length;
    const requested = snapshot.targets.length;
    const degraded = [
      ...(report.refreshErrors.length > 0 ? ['refresh degraded'] : []),
      ...(authorization.kind === 'degraded' || report.auditError != null ? ['audit degraded'] : []),
    ];
    const parts = [
      failed > 0
        ? `${failed} NOT cancelled, ${submitted} cancel${submitted === 1 ? '' : 's'} sent`
        : `${submitted}/${requested} cancels sent`,
      ...(alreadyPending > 0 ? [`${alreadyPending} already cancelling`] : []),
      ...(skipped > 0 ? [`skipped ${skipped} pinned`] : []),
      ...degraded,
      ...(selection.tickerFallbackTargets > 0
        ? [`${selection.tickerFallbackTargets} identified by Kalshi ticker`]
        : []),
    ];
    const lookHint = failed > 0 || report.refreshErrors.length > 0 ? ' — check open orders' : '';
    return outcome('done', `${parts.join(', ')}${lookHint}${polyHint}`, {
      gameLabel,
      requested,
      submitted,
      failed,
      alreadyPending,
      degraded,
      polyResting,
    });
  } finally {
    _pullInFlight = false;
  }
}
